import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Loader2, X, Camera, Check, Info, ChevronDown, ChevronUp } from 'lucide-react';

const STANDARD_OPTIONS = {
  CUSTOM: "Custom Response",
  NOT_APPLICABLE: "Not Applicable", 
  SKIP: "Skip For Now"
};

interface PhotoAttemptState {
  questionId: string;
  attempts: number;
  maxAttempts: number;
  uploadedImages: string[];
  lastRejectionReason: string;
  bestImageSoFar?: {
    imageId: string;
    quality: 'accepted' | 'partial' | 'rejected';
    confidence: number;
  };
}

interface RefineQuestionProps {
  question: {
    QuestionText: string;
    Options?: string[];
    NeedPicture?: boolean;
    AllowMultiple?: boolean;
    id?: string;
  };
  onContinue: (response: string) => Promise<void>;
  onImageUpload?: (file: File) => Promise<string | void>;
  onImageUploaded?: () => void; // Callback when image is successfully uploaded
  onImageAssessment?: (imageId: string, questionId: string, attemptNumber: number) => Promise<any>;
  isSubmitting: boolean;
}

export const RefineQuestionComponent: React.FC<RefineQuestionProps> = ({
  question,
  onContinue,
  onImageUpload,
  onImageUploaded,
  onImageAssessment,
  isSubmitting
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [userInput, setUserInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInputValue, setCustomInputValue] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // Photo attempt tracking
  const [photoAttempts, setPhotoAttempts] = useState<PhotoAttemptState>({
    questionId: question.id || 'unknown',
    attempts: 0,
    maxAttempts: 3,
    uploadedImages: [],
    lastRejectionReason: '',
    bestImageSoFar: undefined
  });
  
  const [assessmentResult, setAssessmentResult] = useState<any>(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [photoAccepted, setPhotoAccepted] = useState(false);
  const [imageAssessing, setImageAssessing] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  // Reset photo state when question changes
  useEffect(() => {
    setUploadedImage(null);
    setImagePreview(null);
    setAssessmentResult(null);
    setShowPhotoOptions(false);
    setPhotoAccepted(false);
    setImageAssessing(false);
    setShowTechnicalDetails(false);
    setPhotoAttempts({
      questionId: question.id || 'unknown',
      attempts: 0,
      maxAttempts: 3,
      uploadedImages: [],
      lastRejectionReason: '',
      bestImageSoFar: undefined
    });
  }, [question.id, question.QuestionText]); // Reset when question ID or text changes

  const handleOptionSelect = (option: string) => {
    if (option === STANDARD_OPTIONS.CUSTOM) {
      setShowCustomInput(true);
      setCustomInputValue('');
      setSelectedOption('');
      setSelectedOptions([]);
    } else if (option === STANDARD_OPTIONS.SKIP) {
      // Skip immediately
      onContinue(option);
    } else {
      if (question.AllowMultiple) {
        // Multi-select mode
        setSelectedOptions(prev => {
          if (prev.includes(option)) {
            return prev.filter(opt => opt !== option);
          } else {
            return [...prev, option];
          }
        });
        setSelectedOption('');
      } else {
        // Single select mode
        setSelectedOption(option);
        setSelectedOptions([]);
      }
      setShowCustomInput(false);
    }
  };

  const handleContinue = async () => {
    if (question.AllowMultiple && selectedOptions.length > 0) {
      await onContinue(selectedOptions.join(', '));
      setSelectedOptions([]);
    } else if (selectedOption) {
      await onContinue(selectedOption);
      setSelectedOption('');
    } else if (userInput.trim()) {
      await onContinue(userInput.trim());
      setUserInput('');
    } else if (question.NeedPicture && uploadedImage) {
      // For image-only questions, continue with a confirmation message
      await onContinue('Image uploaded');
      setUploadedImage(null);
      setImagePreview(null);
    }
  };

  const submitCustomResponse = async () => {
    const customText = customInputValue.trim();
    if (customText) {
      await onContinue(customText);
      setShowCustomInput(false);
      setCustomInputValue('');
    }
  };

  const isStandardOption = (option: string) => {
    return Object.values(STANDARD_OPTIONS).includes(option);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageUpload) {
      try {
        // Create preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        setUploadedImage(file);
        
        // Start assessment loading
        setImageAssessing(true);
        
        // Upload the image
        const result = await onImageUpload(file);
        const imageId = typeof result === 'string' ? result : 'uploaded';
        
        // Update attempt tracking
        const newAttempts = photoAttempts.attempts + 1;
        setPhotoAttempts(prev => ({
          ...prev,
          attempts: newAttempts,
          uploadedImages: [...prev.uploadedImages, imageId]
        }));
        
        // Assess image quality if assessment function is available
        if (onImageAssessment && question.id) {
          const assessment = await onImageAssessment(imageId, question.id, newAttempts);
          setAssessmentResult(assessment);
          
          // Handle assessment result
          if (assessment.status === 'accepted' || newAttempts >= 3) {
            setPhotoAccepted(true);
            setShowPhotoOptions(false);
          } else {
            setShowPhotoOptions(true);
            setPhotoAttempts(prev => ({
              ...prev,
              lastRejectionReason: assessment.feedback || 'Image quality could be improved',
              bestImageSoFar: !prev.bestImageSoFar || assessment.confidence > prev.bestImageSoFar.confidence ?
                { imageId, quality: assessment.status, confidence: assessment.confidence } :
                prev.bestImageSoFar
            }));
          }
        } else {
          // No assessment available - accept immediately
          setPhotoAccepted(true);
        }
        
        // Stop assessment loading
        setImageAssessing(false);
        
        // Notify parent that image was uploaded successfully
        onImageUploaded?.();
        
      } catch (error) {
        console.error('Image upload failed:', error);
        setUploadedImage(null);
        setImagePreview(null);
        setImageAssessing(false);
        // Reset input
        if (event.target) {
          event.target.value = '';
        }
      }
    }
  };

  const handleRetryPhoto = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setShowPhotoOptions(false);
    setAssessmentResult(null);
    setPhotoAccepted(false);
    setImageAssessing(false);
    // Trigger file input click
    document.getElementById('image-upload')?.click();
  };

  const handleAcceptCurrentPhoto = () => {
    setPhotoAccepted(true);
    setShowPhotoOptions(false);
  };

  const handleSkipPhoto = async () => {
    setPhotoAccepted(true);
    setShowPhotoOptions(false);
    await onContinue('Photo skipped - will use standard estimates');
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-8 relative">
        {/* Skip Button - Upper Right Corner */}
        {question.Options?.includes(STANDARD_OPTIONS.SKIP) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOptionSelect(STANDARD_OPTIONS.SKIP)}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            Skip
          </Button>
        )}
        
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-900 leading-relaxed">
            {question.QuestionText}
          </h3>
        </div>

        {/* Multiple Choice Options */}
        {question.Options && question.Options.length > 0 && !showCustomInput && (
          <div className="space-y-3 mb-8">
            {question.AllowMultiple && (
              <div className="text-sm text-gray-600 mb-4 font-medium">
                Select all that apply:
              </div>
            )}
            {question.Options.map((option, index) => {
              // Skip "Custom Response" and "Skip For Now" - they get special treatment
              if (option === STANDARD_OPTIONS.CUSTOM || option === STANDARD_OPTIONS.SKIP) {
                return null;
              }
              
              const isSelected = question.AllowMultiple 
                ? selectedOptions.includes(option)
                : selectedOption === option;
              
              return (
                <Button
                  key={index}
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => handleOptionSelect(option)}
                  disabled={isSubmitting}
                  className={`w-full p-4 h-auto text-left justify-between font-medium ${
                    isSelected 
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex-1">{option}</span>
                  {question.AllowMultiple && isSelected && (
                    <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                  )}
                </Button>
              );
            })}
          </div>
        )}

        {/* Custom Input Section */}
        {showCustomInput && (
          <Card className="mb-8 border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium text-blue-900">Custom Response</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCustomInput(false)}
                  className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <textarea
                placeholder="Please describe your specific requirements..."
                value={customInputValue}
                onChange={(e) => setCustomInputValue(e.target.value)}
                className="w-full p-4 border border-blue-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                rows={4}
                disabled={isSubmitting}
              />
              <div className="flex gap-3 mt-4">
                <Button 
                  onClick={submitCustomResponse}
                  disabled={!customInputValue.trim() || isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Custom Response'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Image Upload Section */}
        {question.NeedPicture && (
          <Card className="mb-8 border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Camera className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">Photo Required</h4>
              </div>
              <p className="text-sm text-blue-700 mb-6">
                Please upload a photo to help provide accurate information for this question.
              </p>
              
              {!imagePreview ? (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center gap-3 hover:text-blue-600 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Camera className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-blue-900">Click to upload photo</div>
                        <div className="text-sm text-blue-600 mt-1">JPG, PNG up to 10MB</div>
                      </div>
                    </label>
                  </div>
                  
                  <div className="text-center">
                    <Button 
                      onClick={handleSkipPhoto} 
                      variant="ghost" 
                      size="sm"
                      disabled={isSubmitting}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Skip Photo - Use Standard Estimates
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative rounded-lg overflow-hidden border border-blue-200 bg-gray-50">
                    <div className="flex items-center justify-center min-h-[200px] max-h-[400px] p-4">
                      <img
                        src={imagePreview}
                        alt="Uploaded photo"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                      />
                    </div>
                    <div className="absolute top-3 right-3 flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setImagePreview(null);
                          setUploadedImage(null);
                        }}
                        className="bg-white/90 hover:bg-white text-gray-600 hover:text-red-600 rounded-full p-2 h-8 w-8 shadow-sm border border-gray-200"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {imageAssessing ? (
                      <div className="flex items-center gap-2 text-blue-700">
                        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                          <Loader2 className="h-3 w-3 animate-spin" />
                        </div>
                        <span className="text-sm font-medium">Analyzing photo quality...</span>
                      </div>
                    ) : photoAccepted ? (
                      <div className="flex items-center gap-2 text-green-700">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </div>
                        <span className="text-sm font-medium">Photo looks great!</span>
                      </div>
                    ) : assessmentResult ? (
                      <div className="flex items-center gap-2 text-red-700">
                        <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                          <Camera className="h-3 w-3" />
                        </div>
                        <span className="text-sm font-medium">
                          {assessmentResult.status === 'rejected' ? 'Photo not usable' : 'Could use a better photo'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-700">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </div>
                        <span className="text-sm font-medium">Photo uploaded successfully</span>
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      {uploadedImage?.name}
                    </div>
                  </div>
                  
                  {/* Detailed feedback about what's wrong */}
                  {assessmentResult && !photoAccepted && (
                    <div className="mt-4 p-3 bg-white/50 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-900 mb-2">
                        {assessmentResult.issues?.includes('Image analysis temporarily unavailable') ? 
                          'Technical issue with photo analysis:' : 
                          'What we need to see:'
                        }
                      </h5>
                      <p className="text-sm text-gray-700 mb-2">{assessmentResult.feedback}</p>
                      
                      {assessmentResult.issues?.includes('Image analysis temporarily unavailable') && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs text-blue-700">
                            💡 <strong>Tip:</strong> You can still continue with this photo, or try uploading a different one. 
                            Photo analysis will help us give more accurate estimates.
                          </p>
                        </div>
                      )}
                      
                      {assessmentResult.issues && assessmentResult.issues.length > 0 && 
                       !assessmentResult.issues.includes('parsing_error') && 
                       !assessmentResult.issues.includes('Image analysis temporarily unavailable') && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-1">Issues found:</p>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {assessmentResult.issues.map((issue: string, index: number) => (
                              <li key={index} className="flex items-start gap-1">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>{issue.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Technical details behind collapsible section */}
                      {(assessmentResult.extractedData?.visibleElements?.length > 0 || 
                        (assessmentResult.extractedData?.datapoints && Object.keys(assessmentResult.extractedData.datapoints).length > 0)) && (
                        <div className="mt-3">
                          <button
                            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <Info className="h-3 w-3" />
                            <span>Technical details</span>
                            {showTechnicalDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                          
                          {showTechnicalDetails && (
                            <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200 text-xs">
                              {assessmentResult.extractedData?.visibleElements && assessmentResult.extractedData.visibleElements.length > 0 && (
                                <div className="mb-3">
                                  <p className="font-medium text-gray-600 mb-1">Elements identified:</p>
                                  <ul className="text-gray-600 space-y-1">
                                    {assessmentResult.extractedData.visibleElements.map((element: string, index: number) => (
                                      <li key={index} className="flex items-start gap-1">
                                        <span className="text-gray-400 mt-0.5">•</span>
                                        <span>{element.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {assessmentResult.extractedData?.datapoints && Object.keys(assessmentResult.extractedData.datapoints).length > 0 && (
                                <div>
                                  <p className="font-medium text-gray-600 mb-1">Extracted datapoints:</p>
                                  <div className="space-y-1">
                                    {Object.entries(assessmentResult.extractedData.datapoints).map(([key, datapoint]: [string, any], index: number) => (
                                      <div key={index} className="text-gray-600 flex justify-between">
                                        <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}:</span>
                                        <span className="ml-2">{datapoint.value} ({Math.round(datapoint.confidence * 100)}%)</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              <div className="mt-2 pt-2 border-t border-gray-200 text-gray-500">
                                <p>This technical information helps our system provide more accurate estimates.</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photo Assessment Feedback - Show immediately after assessment */}
        {(showPhotoOptions || (!photoAccepted && assessmentResult && !imageAssessing)) && assessmentResult && (
          <Card className={`mb-8 ${
            assessmentResult.status === 'rejected' ? 
              (photoAttempts.attempts === 1 ? 'border-red-200 bg-red-50' : 
               photoAttempts.attempts === 2 ? 'border-orange-200 bg-orange-50' : 
               'border-green-200 bg-green-50') :
              (photoAttempts.attempts === 1 ? 'border-yellow-200 bg-yellow-50' : 
               photoAttempts.attempts === 2 ? 'border-blue-200 bg-blue-50' : 
               'border-green-200 bg-green-50')
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Camera className={`h-5 w-5 mt-0.5 ${
                  photoAttempts.attempts === 1 ? 'text-yellow-600' : 
                  photoAttempts.attempts === 2 ? 'text-blue-600' : 
                  'text-green-600'
                }`} />
                <div className="flex-1">
                  <h4 className={`font-medium ${
                    photoAttempts.attempts === 1 ? 'text-red-900' : 
                    photoAttempts.attempts === 2 ? 'text-orange-900' : 
                    'text-green-900'
                  }`}>
                    {assessmentResult.status === 'rejected' ? 
                      (photoAttempts.attempts === 1 ? "This photo won't work for estimates" :
                       photoAttempts.attempts === 2 ? "Still can't get what we need" :
                       "We'll work with what we have") :
                      (photoAttempts.attempts === 1 ? "We can improve this photo" :
                       photoAttempts.attempts === 2 ? "Almost there - one more try?" :
                       "This will work, thanks!")
                    }
                  </h4>
                  <p className={`text-sm mt-1 ${
                    photoAttempts.attempts === 1 ? 'text-red-700' : 
                    photoAttempts.attempts === 2 ? 'text-orange-700' : 
                    'text-green-700'
                  }`}>
                    {assessmentResult.feedback}
                  </p>
                  
                  <div className="flex gap-3 mt-4">
                    {photoAttempts.attempts < 3 && (
                      <Button 
                        onClick={handleRetryPhoto} 
                        size="sm"
                        variant={photoAttempts.attempts === 1 ? "outline" : "default"}
                      >
                        {photoAttempts.attempts === 1 ? `Take Another Photo (2/3 tries)` : 
                         `Final Attempt (3/3)`}
                      </Button>
                    )}
                    
                    {photoAttempts.attempts === 2 && (
                      <Button onClick={handleAcceptCurrentPhoto} size="sm" variant="outline">
                        Use This Photo
                      </Button>
                    )}
                    
                    <Button onClick={handleSkipPhoto} size="sm" variant="ghost">
                      Skip Photo
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photo Quality Approved Message */}
        {question.NeedPicture && photoAccepted && assessmentResult && (
          <Card className="mb-8 border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-green-900">Photo approved!</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Great photo - this will help us provide an accurate estimate. You can continue now.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Text Input for open-ended questions (no options provided and no picture needed) */}
        {(!question.Options || question.Options.length === 0) && !showCustomInput && !question.NeedPicture && (
          <div className="space-y-4 mb-8">
            <Input
              placeholder="Type your response..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isSubmitting && handleContinue()}
              className="w-full p-4 text-lg border-2 rounded-lg focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Action Buttons */}
        {!showCustomInput && (
          <div className="flex justify-center items-center gap-4 pt-6">
            {/* Give Custom Answer Button - To the left of Continue - HIDDEN */}
            {false && question.Options?.includes(STANDARD_OPTIONS.CUSTOM) && (
              <Button 
                onClick={() => handleOptionSelect(STANDARD_OPTIONS.CUSTOM)}
                disabled={isSubmitting}
                variant="outline"
                className="px-6 py-2"
              >
                Give custom answer
              </Button>
            )}
            
            {/* Continue Button - Center */}
            <Button 
              onClick={handleContinue}
              disabled={
                (!selectedOption && 
                 !userInput.trim() && 
                 selectedOptions.length === 0 &&
                 !(question.NeedPicture && photoAccepted)) || 
                isSubmitting ||
                imageAssessing ||
                (question.NeedPicture && !photoAccepted)
              }
              size="lg"
              className="px-12 py-3 text-lg font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : imageAssessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing photo...
                </>
              ) : (question.NeedPicture && !photoAccepted && uploadedImage) ? (
                <>
                  <Camera className="w-5 h-5 mr-2" />
                  Waiting for photo approval
                </>
              ) : (
                <>
                  Continue
                  {question.AllowMultiple && selectedOptions.length > 0 && (
                    <span className="ml-2 text-sm">({selectedOptions.length} selected)</span>
                  )}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};